import { QuickView } from 'src/scripts/sections/product/quick-view'
import { BaseComponent } from 'src/scripts/components/base-component'
import { CartAPI } from 'src/scripts/api/cart-api'
import { fetchConfig } from 'src/scripts/utils/fetch-config'
import { CartDrawer } from 'src/scripts/sections/cart-drawer/cart-drawer'
import { CartPage } from 'src/scripts/sections/cart/cart'
import { BodyElement } from 'src/scripts/components/body-element/body-element'
import { CartNotificationPopup } from 'src/scripts/components/cart-notification/cart-notification-popup'
import { $el, $list } from 'src/scripts/utils/utils'
import { parseHTML, replaceNodeChildren } from 'src/scripts/utils/dom'
import { buildProductFormData } from './product-form-data'

const CART_DRAWER_SELECTOR = '#CartDrawer'
const CART_PAGE_SELECTOR = 'cart-page'
const CART_NOTIFICATION_POPUP_SELECTOR = '#CartNotificationPopup'
const FORM_SELECTOR = 'form'
const VARIANT_INPUT_SELECTOR = '[data-product-form-variant]'
const QUICK_VIEW_SELECTOR = '#Quick-view'
const BODY_ELEMENT_SELECTOR = 'body-element'

export class ProductForm extends BaseComponent {
  cartApi: CartAPI = new CartAPI()
  lastParsedState: string

  mountComponent(): void {
    const form = $el(FORM_SELECTOR, this)
    this.addListener(form, 'submit', this.handleFormSubmit)
  }

  handleFormSubmit = async (event: SubmitEvent): Promise<void> => {
    event.preventDefault()
    const config = this.getQueryConfig()

    this.setDisable(true)
    this.setLoading(true)

    await this.cartApi
      .add(config)
      .then(state => {
        this.setDisable(false)
        this.setLoading(false)
        const parsedState = JSON.parse(state)

        if (parsedState.description) {
          this.showError(parsedState)
          return
        }

        this.lastParsedState = parsedState
        const sectionHTML = parsedState.sections?.[this.cartId]

        if (sectionHTML) {
          this.updateCartItems(parseHTML(sectionHTML))
          return
        }

        const cartDrawer: CartDrawer = $el(CART_DRAWER_SELECTOR)

        if (cartDrawer) {
          return this.cartApi
            .getDrawer()
            .then(drawerHTML => this.updateCartDrawer(parseHTML(drawerHTML)))
        }

        throw new Error(`Missing rendered cart section: ${this.cartId}`)
      })
      .catch(error => console.error('Error in product form component', error))
  }

  updateCartItems(html): void {
    const cartDrawer: CartDrawer = $el(CART_DRAWER_SELECTOR)
    const cart: CartPage = $el(CART_PAGE_SELECTOR)
    const popup: CartNotificationPopup = $el(CART_NOTIFICATION_POPUP_SELECTOR)

    if (cart) {
      this.updateCart(html)
    } else if (cartDrawer) {
      this.updateCartDrawer(html)
    } else if (popup) {
      this.updatePopup(html)
    } else {
      this.updateCartCount(html)
    }
  }

  updateCartCount(html): void {
    const newCount = $el('#NewCartCount', html)
    const currentCounts = $list('#CartCount')

    this.onUpdateCartItems(() => {
      currentCounts.forEach(currentCount => {
        replaceNodeChildren(currentCount, newCount)
      })
    })
  }

  updateCart(html): void {
    const cart: CartPage = $el(CART_PAGE_SELECTOR)

    cart?.updateEmptyStatus(false)
    this.onUpdateCartItems(() => {
      cart?.purchaseHandler(html, this.lastParsedState)
    })
  }

  updateCartDrawer(html): void {
    const cartDrawer: CartDrawer = $el(CART_DRAWER_SELECTOR)

    cartDrawer?.updateAsideProductsByParsedState(this.lastParsedState)
    cartDrawer?.updateEmptyStatus()
    this.onUpdateCartItems(() => {
      cartDrawer?.purchaseHandler(html, this.lastParsedState)
    })
  }

  updatePopup(html): void {
    this.onUpdateCartItems(() => {
      const popup: CartNotificationPopup = $el(CART_NOTIFICATION_POPUP_SELECTOR)
      popup?.purchaseHandler(html, this.lastParsedState)
    })
  }

  async onUpdateCartItems(callback): Promise<void> {
    const quickView: QuickView = $el(QUICK_VIEW_SELECTOR)

    if (quickView?.isOpen) {
      quickView.hide().then(callback)
    } else {
      callback()
    }
  }

  showError(parsedState): void {
    const bodyElement: BodyElement = $el(BODY_ELEMENT_SELECTOR)
    const error =
      typeof parsedState.description === 'object'
        ? this.getErrorFromObject(parsedState.description)
        : parsedState.description

    bodyElement.showNotification(error, 'warning')
  }

  getErrorFromObject(errorObject: object): string {
    return Object.keys(errorObject)
      .map(key => `${key}: ${errorObject[key]}`)
      .join(' , ')
  }

  getQueryConfig() {
    const form: HTMLFormElement = $el(FORM_SELECTOR, this)
    const config: any = fetchConfig('javascript')
    const formData = new FormData(form)

    config.headers['X-Requested-With'] = 'XMLHttpRequest'
    delete config.headers['Content-Type']
    formData.append('sections', [this.cartId])
    formData.append('sections_url', window.location.pathname)

    // 恢复线上 bundle 的组合配件、赠品和主商品批量加购契约。
    config.body = buildProductFormData(formData, form, document)
    return config
  }

  setLoading(isLoading: boolean): void {
    $list('button[type="submit"]', this).forEach(button =>
      button.classList.toggle('loading', isLoading),
    )
  }

  setDisable(isDisable: boolean): void {
    $list('button[type="submit"]', this).forEach(button =>
      button.toggleAttribute('disabled', isDisable),
    )
  }

  setButtonText(text: string): void {
    $list('button[type="submit"]', this).forEach(
      button => (button.textContent = text),
    )
  }

  getVeriantInput() {
    const form = $el(FORM_SELECTOR, this)
    return $el(VARIANT_INPUT_SELECTOR, form)
  }

  get cartId(): string {
    const cartDrawer: CartDrawer = $el(CART_DRAWER_SELECTOR)
    const cart: CartPage = $el(CART_PAGE_SELECTOR)

    if (cartDrawer) return cartDrawer.dataset.sectionId
    if (cart) return cart.dataset.sectionId
    return 'cart-notification-popup-content'
  }
}
